import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { adminLogin } from "@/lib/admin-api";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem("ehc_admin_token");
  if (isLoggedIn) return <Navigate to="/admin" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await adminLogin(email.trim(), password);
      navigate("/admin");
    } catch (ex) {
      const d = ex?.response?.data?.detail;
      let msg;
      if (typeof d === "string") msg = d;
      else if (Array.isArray(d)) msg = d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
      else if (d && typeof d === "object" && d.msg) msg = d.msg;
      else if (ex?.response?.status === 429) msg = "Too many attempts. Try again in 15 minutes.";
      else if (ex?.response?.status === 401) msg = "Invalid email or password.";
      else msg = ex?.message || "Login failed. Check credentials.";
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-navy-900 p-6"
      data-testid="page-admin-login"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md bg-white rounded-3xl p-10 shadow-2xl"
      >
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-100 text-gold-600">
          <Lock className="h-5 w-5" strokeWidth={1.6} />
        </div>
        <h1 className="mt-5 font-serif text-3xl text-navy-900">Admin sign-in</h1>
        <p className="mt-2 text-sm text-navy-500">
          Elite Home Care by Hortense — private dashboard
        </p>

        <label className="flex flex-col gap-2 mt-7">
          <span className="text-sm font-medium text-navy-800">Email</span>
          <input
            type="email"
            data-testid="admin-login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            className="rounded-xl border border-navy-900/15 px-4 py-3 outline-none focus:border-gold-400 focus:ring-4 focus:ring-gold-300/20"
          />
        </label>
        <label className="flex flex-col gap-2 mt-4">
          <span className="text-sm font-medium text-navy-800">Password</span>
          <input
            type="password"
            data-testid="admin-login-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
            className="rounded-xl border border-navy-900/15 px-4 py-3 outline-none focus:border-gold-400 focus:ring-4 focus:ring-gold-300/20"
          />
        </label>
        {err && (
          <div
            data-testid="admin-login-error"
            className="mt-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3"
          >
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="btn-primary w-full mt-6 justify-center"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </div>
  );
}
